import { useEffect, useState } from "react";
import { Upload } from "lucide-react";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  if (!s) return <p className="font-sans-body text-sm text-[#B8B8B8]">Loading settings...</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        ...s,
        shipping_flat: Number(s.shipping_flat),
        shipping_express: Number(s.shipping_express),
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const uploadQr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, instagram_qr_url: data.url });
      await api.put("/admin/settings", { instagram_qr_url: data.url });
      toast.success("Instagram QR code saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const input = (key, label, opts = {}) => (
    <div key={key}>
      <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">{label}</label>
      <input {...opts} value={s[key] ?? ""} onChange={(e) => setS({ ...s, [key]: e.target.value })}
        className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37]"
        data-testid={`settings-${key}-input`} />
    </div>
  );

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif-display text-3xl text-white font-light mb-8">Store Settings</h1>
      <form onSubmit={save} className="space-y-8 max-w-3xl">
        <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
          <h2 className="font-serif-display text-xl text-white">Notifications &amp; Contact</h2>
          {input("notification_email", "Order notification email (new orders are sent here)", { type: "email" })}
          {input("contact_email", "Public contact email (shown on Contact page &amp; footer)", { type: "email" })}
          <p className="font-sans-body text-[11px] text-[#666666] font-light">
            Emails are sent via the platform's managed email service — no extra setup needed.
            Failed sends are logged and can be retried from the Orders page.
          </p>
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
          <h2 className="font-serif-display text-xl text-white">Shipping</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {input("shipping_flat", "Standard shipping ($)", { type: "number", step: "0.01", min: "0" })}
            {input("shipping_express", "Express shipping ($)", { type: "number", step: "0.01", min: "0" })}
          </div>
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
          <h2 className="font-serif-display text-xl text-white">Social</h2>
          {input("instagram_url", "Instagram profile URL")}
          {input("instagram_handle", "Instagram handle")}
          <div>
            <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Instagram QR Code (shown on Contact page)</label>
            <div className="flex items-center gap-4">
              {s.instagram_qr_url && <img src={imageUrl(s.instagram_qr_url)} alt="Instagram QR" className="w-24 h-24 object-contain gold-hairline bg-[#080808] p-2" />}
              <label className="btn-outline-gold cursor-pointer" data-testid="settings-qr-upload-label">
                <Upload size={14} /> Upload QR Image
                <input type="file" accept="image/*" className="hidden" onChange={uploadQr} data-testid="settings-qr-upload-input" />
              </label>
            </div>
          </div>
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
          <h2 className="font-serif-display text-xl text-white">About Page Story</h2>
          <textarea rows={5} value={s.about_story || ""} onChange={(e) => setS({ ...s, about_story: e.target.value })}
            placeholder="Tell your business journey here..."
            className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37] resize-none"
            data-testid="settings-about-story-input" />
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
          <h2 className="font-serif-display text-xl text-white">Policies</h2>
          <p className="font-sans-body text-[11px] text-[#666666] font-light">
            Review and publish your final policy text here. Until you do, the storefront shows a clearly marked draft notice.
          </p>
          {["shipping", "returns", "privacy", "terms"].map((k) => (
            <div key={k}>
              <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2 capitalize">{k} policy</label>
              <textarea rows={4} value={s.policies?.[k] || ""} onChange={(e) => setS({ ...s, policies: { ...(s.policies || {}), [k]: e.target.value } })}
                className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37] resize-none"
                data-testid={`settings-policy-${k}-input`} />
            </div>
          ))}
        </div>

        <button type="submit" className="btn-gold" disabled={saving} data-testid="settings-save-button">
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </form>
    </div>
  );
}
